import { FC, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { Box, Button } from '@mui/material';
import { Alert, Snackbar, StrongModalText } from '@atoms';
import { PERMISSIONS } from '@globalConstants';
import { usePermissions, usePermissionUtil, useURLparams, usePartnerCustomers } from '@hooks';
import { FullWidthContent, MainMenuLayout } from '@layouts';
import { Table } from '@molecules';
import { DEFAULT_ROWS_PER_PAGE, userListColumns } from './constants';
import useAllUsersForFilter from './data/useAllUsersForFilter';
import useApproveUser from './data/useApproveUser/hooks/useApproveUser';
import useGetAllUsersPaginated, { User } from './data/useGetAllUsersPaginated';
import SearchBoxUsers from './searchBoxUsers';
import { EMPTY_FILTERS, UsersSearchFormValues } from './searchBoxUsers/constants';

interface UsersManagmentParams {
  page: string;
  limit: string;
}

/**
 * Check value includes search
 */
const includesIgnoreCase = (value: string | undefined, search: string) =>
  Boolean(value?.toLowerCase().includes(search.toLowerCase()));

/**
 * Filters
 */
const filterUsers = (
  users: User[],
  filters: UsersSearchFormValues,
  companyName?: string
): User[] => {
  const { firstName, lastName, email, customerId } = filters;
  const hasFilters = Boolean(firstName || lastName || email || customerId);

  if (!hasFilters) {
    return users;
  }

  return users.filter((user) => {
    const matchesFirstName = !firstName || includesIgnoreCase(user.firstname, firstName);
    const matchesLastName = !lastName || includesIgnoreCase(user.lastname, lastName);
    const matchesEmail = !email || includesIgnoreCase(user.email, email);
    const matchesCompany =
      !customerId ||
      !companyName ||
      user.companies?.some((company) => includesIgnoreCase(company, companyName));

    return matchesFirstName && matchesLastName && matchesEmail && matchesCompany;
  });
};

/**
 * User management screen
 */
const UsersManagmentScreen: FC = () => {
  const { canAccess } = usePermissionUtil();
  const { params, setURLparams } = useURLparams<UsersManagmentParams>({
    page: '1',
    limit: String(DEFAULT_ROWS_PER_PAGE),
  });
  const navigate = useNavigate();
  const isDemoApprove = usePermissions([PERMISSIONS.demoUpdateWAUsersApprove]);
  const isDemoConfirm = usePermissions([PERMISSIONS.demoUpdateWAUsersConfirm]);
  const canCreateusers = usePermissions([PERMISSIONS.WAUsersCreate]);
  const [filters, setFilters] = useState<UsersSearchFormValues>(EMPTY_FILTERS);
  const [showFilterError, setShowFilterError] = useState(false);
  const { data: customersData } = usePartnerCustomers();

  const hasActiveFilters = Boolean(
    filters.firstName || filters.lastName || filters.email || filters.customerId
  );
  const rowsPerPage = Number(params?.limit || DEFAULT_ROWS_PER_PAGE);
  const currentPage = Number(params?.page || 1);

  const {
    data: response,
    isLoading,
    handlePageChange,
    handleRowsPerPageChange,
    handleRefresh,
  } = useGetAllUsersPaginated({
    params,
    setURLparams,
  });

  const {
    allUsers,
    isLoadingAll,
    error: isFilterLoadError,
    invalidateCache,
  } = useAllUsersForFilter({
    enabled: hasActiveFilters,
    totalCount: response?.pagination?.totalCount,
  });

  useEffect(() => {
    if (isFilterLoadError) {
      setShowFilterError(true);
    }
  }, [isFilterLoadError]);

  const {
    handleSubmit,
    onActionClick,
    isModalOpen,
    selectedUser,
    setIsModalOpen,
    isError,
    setIsError,
    setIsSuccess,
    isSuccess,
    alertMessage,
    isLoading: isLoadingAction,
  } = useApproveUser(() => {
    invalidateCache();
    handleRefresh();
  });

  const selectedCompanyName = useMemo(() => {
    if (!filters.customerId) {
      return undefined;
    }
    return customersData?.find((customer) => String(customer.id) === filters.customerId)?.legalName;
  }, [customersData, filters.customerId]);

  const columns = useMemo(
    () => userListColumns.filter((column) => !column.permission || canAccess(column.permission)),
    [canAccess]
  );

  const filteredUsers = useMemo(() => {
    const sourceUsers = hasActiveFilters ? allUsers : response?.data || [];
    return filterUsers(sourceUsers, filters, selectedCompanyName);
  }, [allUsers, filters, hasActiveFilters, response?.data, selectedCompanyName]);

  const paginatedUsers = useMemo(() => {
    if (!hasActiveFilters) {
      return filteredUsers;
    }

    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredUsers.slice(startIndex, startIndex + rowsPerPage);
  }, [currentPage, filteredUsers, hasActiveFilters, rowsPerPage]);

  const items = useMemo(() => {
    return paginatedUsers.map((item) => ({
      user: item,
      callback: onActionClick,
    }));
  }, [paginatedUsers, onActionClick]);

  /**
   * Applies filters to first page
   */
  const handleSearch = (nextFilters: UsersSearchFormValues) => {
    setFilters(nextFilters);
    setURLparams({
      ...params,
      page: '1',
      limit: String(rowsPerPage),
    });
  };

  return (
    <MainMenuLayout>
      <FullWidthContent>
        {canCreateusers && (
          <Box justifyContent={'flex-end'} display={'flex'} mb={2}>
            <Button variant='contained' onClick={() => navigate('/agregar-usuario')}>
              {t('MENU:USERS_MANAGEMENT:CREATE')}
            </Button>
          </Box>
        )}
        <SearchBoxUsers filters={filters} onSearch={handleSearch} />
        <Table
          columns={columns}
          count={hasActiveFilters ? filteredUsers.length : response?.pagination?.totalCount || 0}
          isLoading={isLoading || isLoadingAll}
          items={items}
          page={params?.page || 1}
          rowsPerPage={params?.limit || DEFAULT_ROWS_PER_PAGE}
          //TODO: Cast the typing to avoid build errors.
          onPageChange={handlePageChange as () => void}
          onRowsPerPageChange={handleRowsPerPageChange as () => void}
        />
        <Alert
          variant='warning'
          modalVisible={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          disabledConfirm={selectedUser.action === 'APPROVE' ? isDemoApprove : isDemoConfirm}
          handleSubmit={handleSubmit}
          closeButtonText='COMMON:CANCEL'
          title={t('PROSPECTS:SEND_EMAIL:MODAL:TITLE')}
          submitLoading={isLoadingAction}
          content={
            <Trans
              i18nKey={`USERS_MANAGEMENT:CONFIRMATION_MODAL:${selectedUser.action}_USER`}
              components={{ s: <StrongModalText /> }}
              values={{ ...selectedUser, count: selectedUser.companies?.length }}
            />
          }
        />
        <Snackbar
          open={showFilterError}
          message={t('USERS_MANAGEMENT:ALERT_MESSAGES:FILTER_LOAD_ERROR')}
          severity='error'
          handleClose={() => setShowFilterError(false)}
        />
        <Snackbar
          open={isError}
          message={t(alertMessage?.description)}
          severity={'error'}
          handleClose={() => setIsError(false)}
        />
        <Snackbar
          open={isSuccess}
          message={t(alertMessage?.description)}
          severity='success'
          handleClose={() => setIsSuccess(false)}
        />
      </FullWidthContent>
    </MainMenuLayout>
  );
};

export default UsersManagmentScreen;
